import { memo, useMemo } from 'react';
import {
  Alert,
  Container,
  FormField,
  Header,
  SpaceBetween,
  Tiles,
} from '@amzn/awsui-components-core';
import { ContentAudience } from '@amzn/awscat-pattern-library-service-model';

import { MetadataTabViewModel } from './MetadataTabViewModel';
import { ContentAudienceOptions } from '../utils/SelectableOptions';

interface MetadataTabAudienceViewProps {
  readonly vm: MetadataTabViewModel;
}

/** Renders the audience tile picker at the bottom of the Metadata tab. */
export const MetadataTabAudienceView = memo(function MetadataTabAudienceView({
  vm,
}: MetadataTabAudienceViewProps) {
  // Project the shared audience option list into the shape Tiles expects,
  // overlaying the per-tile disabled flag computed by the view model.
  const audienceTiles = useMemo(
    () =>
      vm.audienceOptions.map((option) => {
        const display = ContentAudienceOptions.find((o) => o.value === option.value);
        return {
          value: option.value,
          label: display?.label ?? option.value,
          description: display?.description,
          disabled: option.isDisabled || !vm.canEditAudience,
        };
      }),
    [vm.audienceOptions, vm.canEditAudience],
  );

  // Surface both audience-related errors at once so neither is silently dropped.
  const audienceErrorText = [vm.fieldErrors.codeRepoAudience, vm.fieldErrors.codeReview]
    .filter((m): m is string => !!m)
    .join(' ');

  return (
    <Container header={<Header variant="h3">Intended audience</Header>}>
      <SpaceBetween size="s">
        <FormField
          description="Choose who can read this content after publication."
          errorText={audienceErrorText || undefined}
          stretch
        >
          <Tiles
            value={vm.audience ?? ''}
            items={audienceTiles}
            onChange={({ detail }) => vm.changeAudience(detail.value as ContentAudience)}
            readOnly={!vm.canEditAudience}
          />
        </FormField>
        {vm.audienceUpgradeNotice && <Alert type="info">{vm.audienceUpgradeNotice}</Alert>}
      </SpaceBetween>
    </Container>
  );
});
